import { useState } from "react";
import { X, Loader2, Send, Check, AlertTriangle, Mail } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { reportPdfBase64 } from "@/lib/reportPdf";
import { useAuth } from "@/context/AuthContext";

/**
 * EmailModal — sends the resource's PDF as an attachment to a chosen address.
 * POSTs to /api/email/send-document with generous field aliases.
 *
 * The recipient default pulls from the signed-in user's own email
 * (`/auth/me`), so a supervisor sees THEIR own address prefilled — never
 * someone else's. Users edit their email in the Profile page.
 */
export default function EmailModal({ open, onClose, item, kind = "Report", itemLabel, defaultTo }) {
  const { user } = useAuth();
  // Prefer explicit prop → the user's own email → empty. NEVER hard-code an
  // address here; Josh saw his personal email prefilled for other supervisors
  // when that used to be a static default.
  const initialTo = String(defaultTo || user?.email || "").trim();
  const [to, setTo] = useState(initialTo);
  const [subject, setSubject] = useState(`${kind} — ${itemLabel ? itemLabel(item) : (item?.title || item?.id)}`);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!to.trim() || busy) return;
    setBusy(true); setMsg(null);
    try {
      const b64 = await reportPdfBase64(item, kind);
      const filename = `${kind.toLowerCase().replace(/\s+/g, "-")}-${(item.id || "record").toString().slice(0, 8)}.pdf`;
      const html = `<div style="font-family:Arial,sans-serif;font-size:14px;color:#111"><p>${(body || "Please find the attached report.").replace(/\n/g, "<br/>")}</p><p style="color:#666;font-size:12px">Sent from Pit Link Portal</p></div>`;
      // Payload deliberately compact — earlier iterations duplicated the
      // base64 blob into 8 alias fields (attachment.content / data /
      // data_base64 + attachments[] mirrors), which sent 4-8× the PDF size
      // on every send and pushed larger reports past the backend body-size
      // cap. Recipients would then receive a truncated PDF that opened
      // partially or with pages missing. The backend accepts the single
      // canonical schema below; legacy fields kept lean to avoid clipping.
      const payload = {
        kind: String(kind).toLowerCase(),
        doc_id: item.id || item._id,
        to,
        subject,
        html,
        message: body,
        attach_pdf: true,
        pdf_filename: filename,
        asset_id: item.asset_id,
        recipient: to, email: to,
        resource_type: String(kind).toLowerCase(),
        resource_id: item.id || item._id,
        attachments: [{ filename, content: b64, content_type: "application/pdf" }],
      };
      await api.post("/email/send-document", payload);
      setMsg({ type: "ok", text: `Sent to ${to}` });
      setTimeout(onClose, 900);
    } catch (ex) {
      const status = ex?.response?.status;
      const fallback = (status === 404 || status === 405)
        ? "Email service not enabled on this backend — you can Download PDF and share manually."
        : apiError(ex);
      setMsg({ type: "err", text: fallback });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-end md:items-center md:justify-center p-4" onClick={onClose} data-testid="email-modal">
      <form
        onSubmit={submit}
        className="w-full md:max-w-lg panel-raised overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted">Email report</div>
              <div className="font-display font-black text-lg tracking-tight">Send PDF</div>
            </div>
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="field">
            <label>Recipient</label>
            <input type="email" required autoFocus className="input" placeholder="name@company.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to" />
            {!user?.email && (
              <div className="text-[10px] text-muted mt-1">
                Tip · Set a default recipient by adding your email in the <span className="text-white font-bold">Profile</span> page.
              </div>
            )}
          </div>
          <div className="field">
            <label>Subject</label>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
          </div>
          <div className="field">
            <label>Message (optional)</label>
            <textarea className="textarea" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Please review the attached report…" data-testid="email-body" />
          </div>
          {msg && (
            <div className={`pill ${msg.type === "ok" ? "pill-green" : "pill-red"} !text-xs !py-2 !px-3 justify-start`}>
              {msg.type === "ok" ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} {msg.text}
            </div>
          )}
        </div>
        <div className="p-5 flex justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !to.trim()} data-testid="email-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send PDF</>}
          </button>
        </div>
      </form>
    </div>
  );
}
